import { createBrowserRouter, type RouteObject } from "react-router-dom";

import { AccessOutlet } from "./AccessOutlet";
import { AuthGate } from "./AuthGate";
import { ChangePasswordPage } from "./ChangePasswordPage";
import { NotFound, Placeholder } from "./Placeholder";
import { RouteError } from "./RouteError";
import { SCREENS } from "./screens";
import { Shell } from "./Shell";
import { SignInPage } from "./SignInPage";

/**
 * Feature modules register their routes in `src/features/<name>/routes.tsx`, which exports
 * `routes: RouteObject[]` (BUILD-PLAN 2.11.1). The app builds and runs with no feature present.
 */
const featureModules = import.meta.glob<{ routes: RouteObject[] }>("../features/*/routes.tsx", { eager: true });

export function collectFeatureRoutes(modules: Record<string, { routes?: RouteObject[] }> = featureModules): RouteObject[] {
  return Object.values(modules).flatMap((module) => module.routes ?? []);
}

function pathsOf(routes: RouteObject[], prefix = ""): string[] {
  return routes.flatMap((route) => {
    const own = route.path ? (route.path.startsWith("/") ? route.path : `${prefix}/${route.path}`) : prefix;
    return [...(route.path ? [own] : []), ...pathsOf(route.children ?? [], own)];
  });
}

/**
 * The route tree:
 * - the sign-in screen, which anyone may open;
 * - behind the sign-in check: the change-password screen on its own, and everything else inside the
 *   shell: every feature route, then a placeholder for each screen no feature covers.
 */
export function buildRoutes(featureRoutes: RouteObject[] = collectFeatureRoutes()): RouteObject[] {
  const covered = new Set(pathsOf(featureRoutes));
  const placeholders: RouteObject[] = SCREENS.filter((screen) => screen.module !== "F01" && !covered.has(screen.path)).map((screen) => ({
    path: screen.path,
    element: <Placeholder screen={screen} />,
  }));
  return [
    { path: "/sign-in", element: <SignInPage /> },
    {
      element: <AuthGate />,
      children: [
        { path: "/change-password", element: <ChangePasswordPage /> },
        {
          element: <Shell />,
          // A screen that fails to draw is replaced by a plain message; the shell around it stays.
          children: [{ element: <AccessOutlet />, errorElement: <RouteError />, children: [...featureRoutes, ...placeholders, { path: "*", element: <NotFound /> }] }],
        },
      ],
    },
  ];
}

export function createAppRouter() {
  return createBrowserRouter(buildRoutes());
}
